import { z } from 'zod';
import {
  EvalApplicationTeardownSkipSchema,
  EvaluationDatasetSummarySchema,
} from './evaluation-dataset.schemas';

export const EvaluationDatasetGroupMemberSchema = z.object({
  datasetId: z.string(),
  name: z.string(),
  version: z.number(),
  signalCount: z.number().int().nonnegative(),
  redacted: z.boolean(),
  sourceRef: z.string().nullable(),
  tenantLabel: z.string().nullable(),
});

export const EvaluationDatasetGroupSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  datasetIds: z.array(z.string()),
  memberCount: z.number().int().nonnegative(),
  totalSignalCount: z.number().int().nonnegative(),
  members: z.array(EvaluationDatasetGroupMemberSchema),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const EvaluationDatasetGroupDetailSchema =
  EvaluationDatasetGroupSummarySchema.extend({
    datasets: z.array(EvaluationDatasetSummarySchema),
  });

export const ListEvaluationDatasetGroupsResponseSchema = z.object({
  groups: z.array(EvaluationDatasetGroupSummarySchema),
  total: z.number().int().nonnegative(),
});

export const CreateEvaluationDatasetGroupRequestSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  datasetIds: z.array(z.string().min(1)).min(1).max(100),
});

export const CreateEvaluationDatasetGroupResponseSchema = z.object({
  group: EvaluationDatasetGroupDetailSchema,
});

export const PrecisionImportVerdictSchema = z.enum([
  'true_positive',
  'false_positive',
  'false_negative',
]);

export const FalsePositiveImportSignalSchema = z.object({
  signalId: z.string().min(1),
  tenantId: z.string().min(1),
  applicationId: z.string().min(1),
  verdict: PrecisionImportVerdictSchema.optional(),
});

export const CreateEvaluationDatasetGroupFromFalsePositivesRequestSchema =
  z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    signals: z.array(FalsePositiveImportSignalSchema).min(1).max(10_000),
    componentId: z.string().min(1).optional(),
    instantiate: z.boolean().optional().default(true),
  });

export const FalsePositiveImportTenantResultSchema = z.object({
  tenantId: z.string(),
  applicationId: z.string(),
  datasetId: z.string().nullable(),
  signalCount: z.number().int().nonnegative(),
  coveredSignalCount: z.number().int().nonnegative(),
  missingSignalIds: z.array(z.string()),
  error: z.string().nullable(),
});

export const CreateEvaluationDatasetGroupFromFalsePositivesResponseSchema =
  z.object({
    group: EvaluationDatasetGroupDetailSchema.nullable(),
    tenantResults: z.array(FalsePositiveImportTenantResultSchema),
  });

export const UpdateEvaluationDatasetGroupMembersRequestSchema = z.object({
  datasetIds: z.array(z.string().min(1)).min(1).max(100),
});

export const UpdateEvaluationDatasetGroupMembersResponseSchema = z.object({
  group: EvaluationDatasetGroupDetailSchema,
});

export const DeleteEvaluationDatasetGroupRequestSchema = z.object({
  deleteMembers: z.boolean().optional(),
});

export const DeleteEvaluationDatasetGroupResponseSchema = z.object({
  deleted: z.boolean(),
  groupId: z.string(),
  skippedTeardowns: z.array(EvalApplicationTeardownSkipSchema),
  failedMemberIds: z.array(z.string()),
});

export const GetEvaluationDatasetGroupResponseSchema = z.object({
  group: EvaluationDatasetGroupDetailSchema,
});

export type EvaluationDatasetGroupMember = z.infer<
  typeof EvaluationDatasetGroupMemberSchema
>;
export type EvaluationDatasetGroupSummary = z.infer<
  typeof EvaluationDatasetGroupSummarySchema
>;
export type EvaluationDatasetGroupDetail = z.infer<
  typeof EvaluationDatasetGroupDetailSchema
>;
export type FalsePositiveImportSignal = z.infer<
  typeof FalsePositiveImportSignalSchema
>;
export type FalsePositiveImportTenantResult = z.infer<
  typeof FalsePositiveImportTenantResultSchema
>;
